// Orders API Service
import apiClient from './index';

/**
 * Get orders for a picker
 * @param {number} pickerId - Picker ID
 * @returns {Promise} API response with orders data
 */
export const getOrdersByPickerId = async (pickerId) => {
  try {
    const response = await apiClient.get(`/admin/orders`, {
      params: {
        picker_id: pickerId
      }
    });

    // Handle different response formats
    let ordersData = [];
    
    if (Array.isArray(response)) {
      // Response is directly an array
      ordersData = response;
    } else if (response.data) {
      // Response has data property
      if (Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        ordersData = response.data.data;
      }
    } else if (response.success !== false) {
      // Response might have orders directly
      ordersData = response.orders || response.items || [];
    }

    return {
      success: true,
      data: ordersData,
      message: response.message || 'Orders retrieved successfully',
    };
  } catch (error) {
    console.error('Error fetching orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve orders',
      error: error.error || error.message,
      data: [],
    };
  }
};

/**
 * Get order by ID from /api/orders/{orderId} endpoint
 * @param {number} orderId - Order ID
 * @returns {Promise} API response with order data
 */
export const getOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/orders/${orderId}`);

    if (response.success !== false && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Order retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve order',
    };
  } catch (error) {
    console.error('Error fetching order:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve order',
      error: error.error || error.message,
    };
  }
};

/**
 * Update order
 * @param {number} orderId - Order ID
 * @param {object} updateData - Data to update (e.g., { order_status: 'PAID' })
 * @returns {Promise} API response
 */
export const updateOrder = async (orderId, updateData) => {
  try {
    // Base URL already includes /api, so use /orders/{orderId}
    const response = await apiClient.put(`/orders/${orderId}`, updateData);

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Order updated successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to update order',
    };
  } catch (error) {
    console.error('Error updating order:', error);
    return {
      success: false,
      message: error.message || 'Failed to update order',
      error: error.error || error.message,
    };
  }
};

/**
 * Get full order details by ID (public endpoint)
 * @param {number} orderId - Order ID
 * @returns {Promise} API response with complete order data
 */
export const getOrderDetails = async (orderId) => {
  try {
    const response = await apiClient.get(`/orders/${orderId}`);

    // Handle different response formats
    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Order details retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve order details',
    };
  } catch (error) {
    console.error('Error fetching order details:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve order details',
      error: error.error || error.message,
    };
  }
};


